import {
  DASHBOARD_TIMEFRAME_PRESETS,
  MAX_CUSTOM_DASHBOARD_RANGE_DAYS,
} from 'admin-panel/constants/dashboard';
import {
  addDays,
  daysBetween,
  hotelMidnight,
  hotelTimeParts,
} from 'admin-panel/utils/hotel-time';

import type { DashboardTimeframePreset } from 'admin-panel/types/dashboard';

export type DashboardTotalsInterval = 'hourly' | 'daily' | 'weekly';

export type KeenTimeframe = string | { start: string; end: string };

export interface DashboardTimeframe {
  preset: DashboardTimeframePreset;
  timezone: string;
  startDate: string;
  endDateExclusive: string;
  startsAt: string;
  endsAt: string;
  keenTimeframe: KeenTimeframe;
  keenInterval: 'hourly' | 'daily';
  totalsInterval: DashboardTotalsInterval;
}

const KEEN_RELATIVE_TIMEFRAMES: Record<
  Exclude<DashboardTimeframePreset, 'custom'>,
  string
> = {
  today: 'this_1_days',
  yesterday: 'previous_1_days',
  'last-7-days': 'this_7_days',
  'last-30-days': 'this_30_days',
  'this-month': 'this_1_months',
  'last-month': 'previous_1_months',
  'this-year': 'this_1_years',
  'last-year': 'previous_1_years',
};

export function isDashboardTimeframePreset(
  value: string
): value is DashboardTimeframePreset {
  return (DASHBOARD_TIMEFRAME_PRESETS as readonly string[]).includes(value);
}

export function buildDashboardTimeframe({
  preset,
  timezone,
  now = new Date(),
  customStartDate,
  customEndDate,
}: {
  preset: DashboardTimeframePreset;
  timezone: string;
  now?: Date;
  customStartDate?: string;
  customEndDate?: string;
}): DashboardTimeframe {
  const { startDate, endDateExclusive } =
    preset === 'custom'
      ? customDateRange(customStartDate, customEndDate)
      : presetDateRange(preset, hotelTimeParts(now, timezone).date);
  const startsAt = hotelMidnight(startDate, timezone);
  const endsAt = hotelMidnight(endDateExclusive, timezone);
  const totalsInterval = totalsIntervalFor(
    preset,
    daysBetween(startDate, endDateExclusive)
  );

  return {
    preset,
    timezone,
    startDate,
    endDateExclusive,
    startsAt,
    endsAt,
    keenTimeframe:
      preset === 'custom'
        ? { start: startsAt, end: endsAt }
        : KEEN_RELATIVE_TIMEFRAMES[preset],
    keenInterval:
      preset === 'custom' || totalsInterval === 'hourly' ? 'hourly' : 'daily',
    totalsInterval,
  };
}

function presetDateRange(
  preset: Exclude<DashboardTimeframePreset, 'custom'>,
  today: string
) {
  const tomorrow = addDays(today, 1);
  const monthStart = `${today.slice(0, 7)}-01`;
  const yearStart = `${today.slice(0, 4)}-01-01`;

  switch (preset) {
    case 'today':
      return { startDate: today, endDateExclusive: tomorrow };
    case 'yesterday':
      return { startDate: addDays(today, -1), endDateExclusive: today };
    case 'last-7-days':
      return { startDate: addDays(today, -6), endDateExclusive: tomorrow };
    case 'last-30-days':
      return { startDate: addDays(today, -29), endDateExclusive: tomorrow };
    case 'this-month':
      return { startDate: monthStart, endDateExclusive: tomorrow };
    case 'last-month':
      return {
        startDate: `${addDays(monthStart, -1).slice(0, 7)}-01`,
        endDateExclusive: monthStart,
      };
    case 'this-year':
      return { startDate: yearStart, endDateExclusive: tomorrow };
    case 'last-year':
      return {
        startDate: `${Number(today.slice(0, 4)) - 1}-01-01`,
        endDateExclusive: yearStart,
      };
  }
}

function customDateRange(startDate?: string, endDate?: string) {
  if (!isCalendarDate(startDate) || !isCalendarDate(endDate)) {
    throw new Error('Custom dashboard ranges need valid start and end dates.');
  }

  const endDateExclusive = addDays(endDate, 1);
  const dayCount = daysBetween(startDate, endDateExclusive);

  if (dayCount < 1) {
    throw new Error('Custom dashboard ranges must end on or after the start.');
  }

  if (dayCount > MAX_CUSTOM_DASHBOARD_RANGE_DAYS) {
    throw new Error(
      `Custom dashboard ranges are limited to ${MAX_CUSTOM_DASHBOARD_RANGE_DAYS} days.`
    );
  }

  return { startDate, endDateExclusive };
}

function totalsIntervalFor(
  preset: DashboardTimeframePreset,
  dayCount: number
): DashboardTotalsInterval {
  if (preset === 'today' || preset === 'yesterday' || dayCount === 1) {
    return 'hourly';
  }

  return dayCount <= 31 ? 'daily' : 'weekly';
}

function isCalendarDate(value?: string): value is string {
  return Boolean(
    value && /^\d{4}-\d{2}-\d{2}$/.test(value) && addDays(value, 0) === value
  );
}
